import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import AIConcierge from "@/components/AIConcierge";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import CompareTray from "@/components/CompareTray";

import Home from "@/pages/Home";
import LeaseDeals from "@/pages/LeaseDeals";
import DealDetail from "@/pages/DealDetail";
import FindMyCar from "@/pages/FindMyCar";
import Financing from "@/pages/Financing";
import TradeIn from "@/pages/TradeIn";
import HowItWorks from "@/pages/HowItWorks";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Booking from "@/pages/Booking";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Compare from "@/pages/Compare";
import Matchmaker from "@/pages/Matchmaker";
import Negotiate from "@/pages/Negotiate";
import Concierge from "@/pages/Concierge";
import DealsNearMe from "@/pages/DealsNearMe";
import AgreementSign from "@/pages/AgreementSign";
import RealCost from "@/pages/RealCost";
import Dealers from "@/pages/Dealers";
import Exotic from "@/pages/Exotic";
import SeoLanding from "@/pages/SeoLanding";
import DealerPortal from "@/pages/DealerPortal";
import AuthorizedInventory from "@/pages/AuthorizedInventory";
import InventoryDetail from "@/pages/InventoryDetail";
import MarketplaceSearch from "@/pages/MarketplaceSearch";
import Garage from "@/pages/Garage";
import BrokerReply from "@/pages/BrokerReply";
import { useEffect } from "react";

const SEO_SLUGS = [
  "best-bmw-lease-deals",
  "best-mercedes-lease-deals",
  "best-luxury-suv-lease-deals",
  "best-ev-lease-deals",
  "lamborghini-urus-alternatives",
  "porsche-cayenne-alternatives",
  "best-cars-under-500-month",
  "best-cars-under-700-month",
];

function useGoogleAnalytics() {
  const gaId = process.env.REACT_APP_GA_ID;
  const location = useLocation();
  useEffect(() => {
    if (!gaId || typeof window === "undefined") return;
    if (!document.querySelector(`script[src*="${gaId}"]`)) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", gaId, { send_page_view: false });
    }
  }, [gaId]);
  useEffect(() => {
    if (!gaId || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
    });
  }, [location.pathname, location.search, gaId]);
}

function AppShell() {
  const location = useLocation();
  useGoogleAnalytics();

  // CRITICAL: handle Google OAuth callback first (URL fragment), before route matching
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const hideChrome = ["/login", "/signup"].includes(location.pathname);

  return (
    <div className="min-h-screen flex flex-col">
      {!hideChrome && <NavBar />}
      <main className={hideChrome ? "flex-1" : "flex-1 pt-16"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/lease-deals" element={<LeaseDeals />} />
          <Route path="/lease-deals/:dealId" element={<DealDetail />} />
          <Route path="/find-my-car" element={<FindMyCar />} />
          <Route path="/financing" element={<Financing />} />
          <Route path="/trade-in" element={<TradeIn />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/matchmaker" element={<Matchmaker />} />
          <Route path="/negotiate" element={<Negotiate />} />
          <Route path="/concierge" element={<Concierge />} />
          <Route path="/near-me" element={<DealsNearMe />} />
          <Route path="/real-cost" element={<RealCost />} />
          <Route path="/dealers" element={<Dealers />} />
          <Route path="/exotic" element={<Exotic />} />
          <Route path="/inventory" element={<AuthorizedInventory />} />
          <Route path="/inventory/:id" element={<InventoryDetail />} />
          <Route path="/marketplace-search" element={<MarketplaceSearch />} />
          <Route path="/leads/:leadId/reply" element={<BrokerReply />} />
          <Route path="/dealer-portal" element={<DealerPortal />} />
          {SEO_SLUGS.map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<SeoLanding slug={slug} />} />
          ))}
          <Route path="/agreements/:agId" element={<ProtectedRoute><AgreementSign /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/garage" element={<ProtectedRoute><Garage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
      {!hideChrome && <AIConcierge />}
      {!hideChrome && <CompareTray />}
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}
